import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Avatar,
  Stack,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Badge,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Group as GroupIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  People as PeopleIcon,
  SmartToy as BotIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Visibility as ViewIcon,
  Lock as PrivateIcon,
  Public as PublicIcon,
  ContentCopy as CopyIcon,
  PlayArrow as RunIcon,
  Settings as SettingsIcon,
  Timeline as ActivityIcon,
  Security as PermissionIcon,
} from '@mui/icons-material';

function AgentGroups({ darkMode }) {
  const theme = useTheme();
  const [groups, setGroups] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    visibility: 'private',
    tags: [],
    agents: [],
  });

  // Sample data
  useEffect(() => {
    setGroups([
      {
        id: '1',
        name: 'Customer Service Agents',
        description: 'Specialized agents for handling customer inquiries and support',
        agentCount: 5,
        visibility: 'public',
        starred: true,
        tags: ['customer-service', 'support', 'chat'],
        owner: 'John Doe',
        members: ['Alice', 'Bob', 'Charlie'],
        lastActivity: '2024-01-15T10:30:00Z',
        agents: [
          {
            id: '1', name: 'Support Bot', type: 'chat', status: 'active',
          },
          {
            id: '2', name: 'FAQ Assistant', type: 'knowledge', status: 'active',
          },
          {
            id: '3', name: 'Ticket Router', type: 'routing', status: 'active',
          },
        ],
      },
      {
        id: '2',
        name: 'Data Analysis Team',
        description: 'Agents focused on data processing and analytics',
        agentCount: 3,
        visibility: 'private',
        starred: false,
        tags: ['analytics', 'data', 'reporting'],
        owner: 'Jane Smith',
        members: ['David', 'Eve'],
        lastActivity: '2024-01-14T15:45:00Z',
        agents: [
          {
            id: '4', name: 'Report Generator', type: 'analysis', status: 'active',
          },
          {
            id: '5', name: 'Data Validator', type: 'validation', status: 'active',
          },
        ],
      },
      {
        id: '3',
        name: 'Creative Writing Assistants',
        description: 'AI agents for content creation and writing tasks',
        agentCount: 4,
        visibility: 'public',
        starred: true,
        tags: ['writing', 'content', 'creative'],
        owner: 'Mike Johnson',
        members: ['Frank', 'Grace'],
        lastActivity: '2024-01-13T09:20:00Z',
        agents: [
          {
            id: '6', name: 'Blog Writer', type: 'writing', status: 'active',
          },
          {
            id: '7', name: 'Email Assistant', type: 'communication', status: 'active',
          },
        ],
      },
    ]);
  }, []);

  const handleCreateGroup = () => {
    setCreateDialogOpen(true);
    setNewGroup({
      name: '',
      description: '',
      visibility: 'private',
      tags: [],
      agents: [],
    });
  };

  const handleEditGroup = (group) => {
    setSelectedGroup(group);
    setNewGroup({
      name: group.name,
      description: group.description,
      visibility: group.visibility,
      tags: group.tags,
      agents: group.agents,
    });
    setEditDialogOpen(true);
  };

  const handleDeleteGroup = (groupId) => {
    setGroups(groups.filter((g) => g.id !== groupId));
  };

  const handleStarToggle = (groupId) => {
    setGroups(groups.map((g) => (g.id === groupId ? { ...g, starred: !g.starred } : g)));
  };

  const filteredGroups = groups.filter((group) => {
    switch (selectedTab) {
      case 0: return true; // All
      case 1: return group.starred; // Starred
      case 2: return group.visibility === 'public'; // Public
      case 3: return group.visibility === 'private'; // Private
      default: return true;
    }
  });

  function GroupCard({ group }) {
    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{ flex: 1 }}>
          <Box sx={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2,
          }}
          >
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, flex: 1,
            }}
            >
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <GroupIcon />
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {group.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  by
                  {' '}
                  {group.owner}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => handleStarToggle(group.id)}
                color={group.starred ? 'warning' : 'default'}
              >
                {group.starred ? <StarIcon /> : <StarBorderIcon />}
              </IconButton>

              <IconButton
                size="small"
                onClick={(e) => {
                  setMenuAnchor(e.currentTarget);
                  setSelectedGroup(group);
                }}
              >
                <MoreIcon />
              </IconButton>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {group.description}
          </Typography>

          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2, mb: 2,
          }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BotIcon fontSize="small" color="primary" />
              <Typography variant="body2">
                {group.agentCount}
                {' '}
                agents
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PeopleIcon fontSize="small" color="primary" />
              <Typography variant="body2">
                {group.members.length}
                {' '}
                members
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {group.visibility === 'public' ? (
                <PublicIcon fontSize="small" color="success" />
              ) : (
                <PrivateIcon fontSize="small" color="warning" />
              )}
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {group.visibility}
              </Typography>
            </Box>
          </Box>

          <Box sx={{
            display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2,
          }}
          >
            {group.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary">
            Last activity:
            {' '}
            {new Date(group.lastActivity).toLocaleDateString()}
          </Typography>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button
            size="small"
            startIcon={<ViewIcon />}
            onClick={() => { /* Navigate to group details */ }}
          >
            View
          </Button>

          <Box>
            <Button
              size="small"
              startIcon={<RunIcon />}
              color="primary"
              sx={{ mr: 1 }}
            >
              Run All
            </Button>

            <Button
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => handleEditGroup(group)}
            >
              Manage
            </Button>
          </Box>
        </CardActions>
      </Card>
    );
  }

  return (
    <Box sx={{
      p: 3, height: '100%', display: 'flex', flexDirection: 'column',
    }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
      }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Agent Groups
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Organize and manage collections of AI agents for different purposes
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateGroup}
          size="large"
        >
          Create Group
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                All Groups
                <Chip label={groups.length} size="small" />
              </Box>
            )}
          />
          <Tab
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon fontSize="small" />
                Starred
                <Chip label={groups.filter((g) => g.starred).length} size="small" />
              </Box>
            )}
          />
          <Tab
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PublicIcon fontSize="small" />
                Public
                <Chip label={groups.filter((g) => g.visibility === 'public').length} size="small" />
              </Box>
            )}
          />
          <Tab
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PrivateIcon fontSize="small" />
                Private
                <Chip label={groups.filter((g) => g.visibility === 'private').length} size="small" />
              </Box>
            )}
          />
        </Tabs>
      </Box>

      {/* Groups Grid */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {filteredGroups.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No agent groups found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first agent group to organize your AI agents
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateGroup}>
              Create Group
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredGroups.map((group) => (
              <Grid item xs={12} sm={6} lg={4} key={group.id}>
                <GroupCard group={group} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); /* View group */ }}>
          <ViewIcon sx={{ mr: 1 }} />
          {' '}
          View Details
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); handleEditGroup(selectedGroup); }}>
          <EditIcon sx={{ mr: 1 }} />
          {' '}
          Edit Group
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); setShareDialogOpen(true); }}>
          <ShareIcon sx={{ mr: 1 }} />
          {' '}
          Share Group
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); /* Copy group */ }}>
          <CopyIcon sx={{ mr: 1 }} />
          {' '}
          Duplicate
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            handleDeleteGroup(selectedGroup?.id);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          {' '}
          Delete Group
        </MenuItem>
      </Menu>

      {/* Create/Edit Group Dialog */}
      <Dialog
        open={createDialogOpen || editDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editDialogOpen ? 'Edit Agent Group' : 'Create New Agent Group'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Group Name"
              value={newGroup.name}
              onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={newGroup.description}
              onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />

            <FormControl fullWidth>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={newGroup.visibility}
                onChange={(e) => setNewGroup({ ...newGroup, visibility: e.target.value })}
                label="Visibility"
              >
                <MenuItem value="private">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PrivateIcon />
                    {' '}
                    Private
                  </Box>
                </MenuItem>
                <MenuItem value="public">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PublicIcon />
                    {' '}
                    Public
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Tags (comma-separated)"
              value={newGroup.tags.join(', ')}
              onChange={(e) => setNewGroup({
                ...newGroup,
                tags: e.target.value.split(',').map((tag) => tag.trim()).filter(Boolean),
              })}
              fullWidth
              placeholder="e.g., customer-service, support, automation"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setEditDialogOpen(false);
          }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // Handle save logic here
              setCreateDialogOpen(false);
              setEditDialogOpen(false);
            }}
          >
            {editDialogOpen ? 'Update' : 'Create'}
            {' '}
            Group
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AgentGroups;
